import { useSubscribeToTicketListMutations } from "@jugl-web/domain-resources/tickets/hooks/useSubscribeToTicketListMutations";
import { useTicketList } from "@jugl-web/domain-resources/tickets/hooks/useTicketList";
import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import { TicketSortDirection } from "@jugl-web/rest-api/tickets/models/common-types/TicketSortDirection";
import {
  useLocalStorage,
  useSessionStorage,
} from "@jugl-web/utils/hooks/useStorage";
import {
  TICKETS_VIEW_SEARCH_QUERY_KEY,
  TICKETS_VIEW_SORT_KEY,
} from "@jugl-web/utils/storage";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, ReactNode, useMemo } from "react";
import {
  TicketsViewContext,
  TicketsViewContextValue,
} from "./TicketsViewContext";

interface TicketsViewProviderProps {
  children: ReactNode;
  filter: TicketInboxFilter;
}

export const TicketsViewProvider: FC<TicketsViewProviderProps> = ({
  children,
  filter,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { id: meId = "" } = useMe();

  const [searchQuery, setSearchQuery] = useSessionStorage<string>(
    TICKETS_VIEW_SEARCH_QUERY_KEY,
    ""
  );

  const [sort, setSort] = useLocalStorage<TicketSortDirection>(
    TICKETS_VIEW_SORT_KEY,
    "desc"
  );

  const ticketListQuery = useTicketList({
    entityId,
    filter,
    meId,
    searchQuery,
    // For "todo-today" filter, we want to sort tickets by due date in ascending order to show the most urgent tickets first.
    // For other filters, we use the stored sort direction.
    sort: filter === "todo-today" ? "asc" : sort,
    skip: !meId,
  });

  const { hasMore, setTickets, setTotalCount } = ticketListQuery;

  useSubscribeToTicketListMutations({
    entityId,
    sort: filter === "todo-today" ? "asc" : sort,
    hasMore,
    filter,
    meId,
    searchQuery,
    setTickets,
    setTotalCount,
  });

  const contextValue = useMemo<TicketsViewContextValue>(
    () => ({
      ticketListQuery,
      searchQuery,
      sort,
      setSearchQuery,
      setSort,
    }),
    [ticketListQuery, searchQuery, sort, setSearchQuery, setSort]
  );

  return (
    <TicketsViewContext.Provider value={contextValue}>
      {children}
    </TicketsViewContext.Provider>
  );
};
